import dayjs from "dayjs";
import { searchFlights } from "./fhpService";
import {
  dealsListFromSearchFlightsResponseBody,
  sortFlightDealsByPerPersonAscending,
} from "../utils/flightParallelSearchHelpers";
import {
  buildFlightOfferPricingDisplay,
  buildRouteLabel,
  buildFlightCardLegRowsFromDeal,
  getFlightSegmentsWithLegs,
} from "../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import {
  airlineDisplayNameFromCode,
  buildAirlineNameMap,
} from "../utils/airlineNameFromCode";
import { belvedereContract } from "../data/belvedereContractData";
import {
  buildPriceBreakdown,
  buildBelvedereCampaignPricingSsot,
  formatEur,
  formatUsd,
  resolveBelvedereFlightIlsFromBreakdown,
  countBelvedereCampaignPassengers,
  normalizeBelvedereCampaignRoomOccupancy,
} from "./belvederePricing";

function attachQuotePackagePricingSsot(
  quote,
  {
    flightUsd = 0,
    flightDeal = null,
    eurToIls,
    usdToIls,
    searchRooms,
    agentChannel = false,
    publicChannel = false,
    arrivalDate = null,
    departureDate = null,
    /** Flight-side fee pax (searchable capacity when party was capped). */
    flightSearchPax = null,
  } = {},
) {
  const breakdown = {
    hotelNet: quote.netTotal,
    hotelTotal: quote.sellingTotal,
    ...(flightUsd > 0 ? { flightNet: flightUsd } : {}),
  };
  const partyPax = countCampaignSearchPax(searchRooms);
  const ssot = buildBelvedereCampaignPricingSsot(breakdown, {
    eurToIls,
    usdToIls,
    agentChannel,
    publicChannel,
    occupancies: searchRooms,
    roomBreakdown: quote.roomBreakdown,
    arrivalDate,
    departureDate,
    flightDeal,
    // Hidden flight markup / HR fee follows the seats we actually searched.
    totalPax: flightSearchPax != null ? flightSearchPax : partyPax,
  });
  return {
    packageWeightedIls: ssot?.finalSalePriceIls ?? null,
    passengerPricing: ssot?.passengerPricing ?? null,
  };
}

export const BELVEDERE_FLIGHT_DESTINATION = "HER";

/** LDS flight search can be slow; fail fast so the campaign UI does not hang forever. */
export const BELVEDERE_FLIGHT_SEARCH_TIMEOUT_MS = 75_000;

export class BelvedereFlightSearchTimeoutError extends Error {
  constructor() {
    super("BELVEDERE_FLIGHT_SEARCH_TIMEOUT");
    this.name = "BelvedereFlightSearchTimeoutError";
  }
}

export function withBelvedereFlightSearchTimeout(
  promise,
  ms = BELVEDERE_FLIGHT_SEARCH_TIMEOUT_MS,
) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new BelvedereFlightSearchTimeoutError()), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Campaign flights sold at LDS net — no per-pax markup. */
export const BELVEDERE_FLIGHT_MARKUP_USD_PER_PAX = 0;

export const BELVEDERE_FLIGHT_ORIGINS = [{ code: "TLV", label: "תל אביב (TLV)" }];

/** שמות עבריים ליעדי קמפיין Belvedere (תצוגת סיכום). */
export const BELVEDERE_CAMPAIGN_AIRPORT_HE = {
  TLV: "תל אביב",
  HER: "כרתים",
  TBS: "טביליסי",
  ETM: "אילת",
  HFA: "חיפה",
};

export function formatBelvedereCampaignAirportLabel(code) {
  const normalized = String(code ?? "").trim().toUpperCase();
  if (!normalized || normalized === "—") return "—";
  const he = BELVEDERE_CAMPAIGN_AIRPORT_HE[normalized];
  return he ? `${he} (${normalized})` : normalized;
}

export function normalizeCampaignAirportCode(value) {
  const raw = String(value ?? "").trim().toUpperCase();
  if (!raw || raw === "?" || raw === "—") return "";
  const paren = raw.match(/\(([A-Z]{3})\)/);
  if (paren) return paren[1];
  if (/^[A-Z]{3}$/.test(raw)) return raw;
  return "";
}

/**
 * True when the campaign flight option actually serves the requested IATA
 * (outbound arrival, and return origin when present).
 */
export function campaignFlightServesDestination(flight, destination) {
  const expected = normalizeCampaignAirportCode(destination);
  if (!expected) return true;
  if (!flight || typeof flight !== "object") return false;
  const rows = Array.isArray(flight.legRows) ? flight.legRows : [];
  if (rows.length > 0) {
    const outboundArr = normalizeCampaignAirportCode(rows[0]?.destinationCode);
    if (outboundArr && outboundArr !== expected) return false;
    if (rows.length > 1) {
      const inboundDep = normalizeCampaignAirportCode(rows[1]?.originCode);
      if (inboundDep && inboundDep !== expected) return false;
    }
    if (outboundArr) return outboundArr === expected;
  }
  const mappedDest = normalizeCampaignAirportCode(flight.destination);
  return Boolean(mappedDest) && mappedDest === expected;
}

export const CAMPAIGN_FLIGHT_DESTINATION_MISMATCH_MESSAGE =
  "הטיסות שנבחרו אינן ליעד הקמפיין. חפשו שוב טיסות ליעד המלון לפני שמירת ההצעה.";

export function assertCampaignFlightsMatchDestination(flights, destination) {
  const expected = normalizeCampaignAirportCode(destination);
  if (!expected) return;
  const list = Array.isArray(flights) ? flights : flights ? [flights] : [];
  if (list.length === 0) return;
  if (list.some((flight) => !campaignFlightServesDestination(flight, expected))) {
    throw new Error(CAMPAIGN_FLIGHT_DESTINATION_MISMATCH_MESSAGE);
  }
}

function isoToLdsDate(iso) {
  if (!iso) return "";
  const parsed = dayjs(iso, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
}

/**
 * LDS/GDS flight availability is 1–9 (9 means 9+). Searching for more seat
 * passengers than 9 returns empty even when flights still have ≥9 seats.
 * There is no multi-PNR party-split in this campaign — we search at this cap
 * and return package results for that searchable flight capacity.
 */
export const BELVEDERE_LDS_FLIGHT_SEARCH_MAX_SEATS = 9;

function roomsToLdsCompositions(rooms) {
  return (rooms || []).map((room) => {
    const normalized = normalizeBelvedereCampaignRoomOccupancy(room);
    const childAges = normalized.childAges;
    const infants = childAges.filter((age) => age >= 0 && age <= 1.99).length;
    const kidsAges = childAges.filter((age) => age > 1.99);
    return {
      adults: normalized.adults,
      kids: kidsAges.length,
      infants,
      kidsAges,
    };
  });
}

function countLdsCompositionSeatPax(compositions) {
  return (compositions || []).reduce(
    (sum, room) => sum + (Number(room?.adults) || 0) + (Number(room?.kids) || 0),
    0,
  );
}

function countLdsCompositionTotalPax(compositions) {
  return (compositions || []).reduce(
    (sum, room) =>
      sum +
      (Number(room?.adults) || 0) +
      (Number(room?.kids) || 0) +
      (Number(room?.infants) || 0),
    0,
  );
}

/**
 * Reduce adults+kids to ≤ maxSeats so LDS still returns flights with ≥9 seats.
 * Trims kids first, then adults (keeps ≥1 adult when any were requested).
 */
export function capBelvedereFlightSearchCompositions(
  compositions,
  maxSeats = BELVEDERE_LDS_FLIGHT_SEARCH_MAX_SEATS,
) {
  const list = Array.isArray(compositions)
    ? compositions.map((room) => ({
        adults: Math.max(0, Math.floor(Number(room?.adults) || 0)),
        kids: Math.max(0, Math.floor(Number(room?.kids) || 0)),
        infants: Math.max(0, Math.floor(Number(room?.infants) || 0)),
        kidsAges: Array.isArray(room?.kidsAges) ? room.kidsAges.slice() : [],
      }))
    : [];
  if (list.length === 0) return list;

  let seatPax = countLdsCompositionSeatPax(list);
  if (seatPax <= maxSeats) return list;

  let excess = seatPax - maxSeats;
  for (let i = list.length - 1; i >= 0 && excess > 0; i -= 1) {
    const trimKids = Math.min(list[i].kids, excess);
    if (trimKids > 0) {
      list[i].kids -= trimKids;
      list[i].kidsAges = list[i].kidsAges.slice(0, list[i].kids);
      excess -= trimKids;
    }
  }
  for (let i = list.length - 1; i >= 0 && excess > 0; i -= 1) {
    const trimAdults = Math.min(list[i].adults, excess);
    if (trimAdults > 0) {
      list[i].adults -= trimAdults;
      excess -= trimAdults;
    }
  }
  // Drop empty rooms created by trimming (keep at least one adult overall).
  let nonEmpty = list.filter(
    (room) => room.adults + room.kids + room.infants > 0,
  );
  if (nonEmpty.length === 0) {
    nonEmpty = [{ adults: 1, kids: 0, infants: 0, kidsAges: [] }];
  } else if (nonEmpty.every((room) => room.adults === 0)) {
    nonEmpty[0].adults = 1;
  }
  return nonEmpty;
}

function countTotalPax(rooms) {
  return countBelvedereCampaignPassengers(rooms);
}

export function countRoomOccupancyPax(occupancy) {
  return countBelvedereCampaignPassengers([occupancy]);
}

export function countCampaignSearchPax(rooms) {
  return countBelvedereCampaignPassengers(rooms);
}

export function calculateBelvedereFlightMarkup(totalPax) {
  const pax = Math.max(0, Math.floor(Number(totalPax)) || 0);
  return Math.round(pax * BELVEDERE_FLIGHT_MARKUP_USD_PER_PAX * 100) / 100;
}

export function formatPackageIls(amount) {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function estimatePackagePerPersonIls(totalIls, rooms) {
  const total = Number(totalIls) || 0;
  const pax = Math.max(1, countTotalPax(rooms));
  return Math.round(total / pax);
}

export function formatPackageIlsPerPerson(totalIls, rooms) {
  return formatPackageIls(estimatePackagePerPersonIls(totalIls, rooms));
}

/** EUR reference for package ILS display (admin EUR rate). */
export function packageIlsToEurReference(ilsAmount, eurRate) {
  const ils = Number(ilsAmount) || 0;
  const rate = Number(eurRate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return Math.round((ils / rate) * 100) / 100;
}

export function formatPackageIlsWithEurInParens(ilsAmount, fxRate, currency = "EUR") {
  const ilsLabel = formatPackageIls(ilsAmount);
  const foreignRef = packageIlsToEurReference(ilsAmount, fxRate);
  if (foreignRef == null) return ilsLabel;
  const foreignLabel = currency === "USD" ? formatUsd(foreignRef) : formatEur(foreignRef);
  return `${ilsLabel} (${foreignLabel})`;
}

export function formatPackageIlsPerPersonWithEurInParens(totalIls, rooms, eurRate) {
  return formatPackageIlsWithEurInParens(estimatePackagePerPersonIls(totalIls, rooms), eurRate);
}

export function enrichRoomBreakdownPackagePricing(
  roomBreakdown,
  searchRooms,
  flightSellingUsd,
  {
    eurToIls,
    usdToIls,
    agentChannel = false,
    publicChannel = false,
    /** When party was capped for LDS, fee/markup pax follows searchable seats. */
    flightSearchPax = null,
  } = {},
) {
  if (!Array.isArray(roomBreakdown) || roomBreakdown.length === 0) return roomBreakdown;

  const partyPax = Math.max(1, countTotalPax(searchRooms));
  const feePax =
    flightSearchPax != null
      ? Math.max(1, Math.floor(Number(flightSearchPax)) || 1)
      : partyPax;
  const flightUsd = Number(flightSellingUsd) || 0;
  const eurRate = Number(eurToIls);
  const usdRate = Number(usdToIls);
  const hasRates = Number.isFinite(eurRate) && eurRate > 0;

  const flightPricing =
    flightUsd > 0 && Number.isFinite(usdRate) && usdRate > 0
      ? resolveBelvedereFlightIlsFromBreakdown(flightUsd, usdRate, feePax, {
          agentChannel,
          publicChannel,
        })
      : null;

  return roomBreakdown.map((row, index) => {
    const occupancy = searchRooms?.[index] ?? searchRooms?.[row.roomIndex - 1];
    const roomPax = Math.max(1, countRoomOccupancyPax(occupancy));
    const hotelEur = Number(row.sellingTotal) || 0;

    if (!hasRates) {
      return { ...row, roomPax, packageWeightedIls: null, packageWeightedIlsPerPerson: null };
    }

    const hotelIls = hotelEur * eurRate;
    const flightIlsShare = flightPricing?.flightNetIls
      ? (flightPricing.flightNetIls * roomPax) / partyPax
      : 0;
    const packageWeightedIls = Math.round((hotelIls + flightIlsShare) * 100) / 100;
    const packageWeightedIlsPerPerson = Math.round(packageWeightedIls / roomPax);

    return {
      ...row,
      roomPax,
      packageWeightedIls,
      packageWeightedIlsPerPerson,
    };
  });
}

export function estimatePackageTotalIls(
  hotelSellingEur,
  flightSellingUsd,
  eurToIls,
  usdToIls,
  { totalPax = 1, agentChannel = false, publicChannel = false } = {},
) {
  const hotelEur = Number(hotelSellingEur) || 0;
  const flightUsd = Number(flightSellingUsd) || 0;
  const eurRate = Number(eurToIls);
  const usdRate = Number(usdToIls);
  if (!Number.isFinite(eurRate) || eurRate <= 0) return null;

  let total = hotelEur * eurRate;
  if (flightUsd > 0 && Number.isFinite(usdRate) && usdRate > 0) {
    const flightPricing = resolveBelvedereFlightIlsFromBreakdown(flightUsd, usdRate, totalPax, {
      agentChannel,
      publicChannel,
    });
    total += flightPricing.flightNetIls;
  }
  return Math.round(total * 100) / 100;
}

export function estimateBelvedereCampaignFinalSaleIls(
  hotelNet,
  roomCount,
  nights,
  selectedFlight = null,
  { eurToIls, usdToIls, agentAddedCommissionPercent = 0 } = {},
) {
  const breakdown = buildPackagePriceBreakdown(hotelNet, roomCount, nights, selectedFlight);
  const ssot = buildBelvedereCampaignPricingSsot(breakdown, {
    eurToIls,
    usdToIls,
    agentAddedCommissionPercent,
  });
  return ssot?.finalSalePriceIls ?? null;
}

/** Hebrew label for a room's proportional share of the package total. */
export function belvedereRoomPackageShareLabelHe(roomIndex, { includeFlights = false } = {}) {
  const index = Number(roomIndex);
  const roomPart = Number.isFinite(index) && index > 0 ? `לחדר ${index}` : "לחדר";
  return includeFlights
    ? `חלק יחסי ${roomPart} כולל טיסות`
    : `חלק יחסי ${roomPart}`;
}

/**
 * Nudge the last priced room so Σ packageWeightedIls matches the displayed package total
 * (avoids ₪0.01–0.02 drift from per-room rounding).
 */
export function reconcileRoomPackageSharesToTotal(roomBreakdown, targetTotalIls) {
  if (!Array.isArray(roomBreakdown) || roomBreakdown.length === 0) return roomBreakdown;
  const target = Math.round((Number(targetTotalIls) || 0) * 100) / 100;
  if (!(target > 0)) return roomBreakdown;

  let lastPricedIndex = -1;
  let sum = 0;
  for (let i = 0; i < roomBreakdown.length; i += 1) {
    const value = Number(roomBreakdown[i]?.packageWeightedIls);
    if (!Number.isFinite(value)) continue;
    sum += value;
    lastPricedIndex = i;
  }
  if (lastPricedIndex < 0) return roomBreakdown;

  const roundedSum = Math.round(sum * 100) / 100;
  const diff = Math.round((target - roundedSum) * 100) / 100;
  if (diff === 0) return roomBreakdown;

  return roomBreakdown.map((row, index) => {
    if (index !== lastPricedIndex) return row;
    const next = Math.round((Number(row.packageWeightedIls) + diff) * 100) / 100;
    const roomPax = Math.max(1, Number(row.roomPax) || 1);
    return {
      ...row,
      packageWeightedIls: next,
      packageWeightedIlsPerPerson: Math.round((next / roomPax) * 100) / 100,
    };
  });
}

export function scaleRoomBreakdownToFinalSalePrice(
  roomBreakdown,
  agentNetPriceIls,
  finalSalePriceIls,
) {
  if (!Array.isArray(roomBreakdown) || !agentNetPriceIls || agentNetPriceIls <= 0) {
    return roomBreakdown;
  }
  const ratio = (Number(finalSalePriceIls) || 0) / agentNetPriceIls;
  if (!Number.isFinite(ratio) || ratio <= 0) return roomBreakdown;

  const scaled = roomBreakdown.map((row) => {
    if (row.packageWeightedIls == null) return row;
    const packageWeightedIls = Math.round(row.packageWeightedIls * ratio * 100) / 100;
    const packageWeightedIlsPerPerson =
      row.packageWeightedIlsPerPerson != null
        ? Math.round(row.packageWeightedIlsPerPerson * ratio * 100) / 100
        : null;
    return { ...row, packageWeightedIls, packageWeightedIlsPerPerson };
  });
  return reconcileRoomPackageSharesToTotal(scaled, finalSalePriceIls);
}

export function enrichFlightOffersWithPackagePricing(
  flights,
  hotelSellingEur,
  { eurToIls, usdToIls } = {},
) {
  const hotel = Number(hotelSellingEur) || 0;
  if (!hotel || !Array.isArray(flights) || flights.length === 0) return flights;

  return [...flights]
    .map((flight) => {
      const weightedIls = estimatePackageTotalIls(
        hotel,
        flight.sellingPrice,
        eurToIls,
        usdToIls,
      );
      return {
        ...flight,
        packageHotelSellingEur: hotel,
        packageWeightedIls: weightedIls,
      };
    })
    .sort(
      (a, b) =>
        (a.packageWeightedIls ?? Number.POSITIVE_INFINITY) -
        (b.packageWeightedIls ?? Number.POSITIVE_INFINITY),
    );
}

export function pickCheapestFlight(flights) {
  if (!Array.isArray(flights) || flights.length === 0) return null;
  return [...flights].sort(
    (a, b) => (Number(a.sellingPrice) || 0) - (Number(b.sellingPrice) || 0),
  )[0];
}

export function enrichRoomOffersWithBundledFlight(
  roomOffers,
  flight,
  {
    eurToIls,
    usdToIls,
    searchRooms,
    agentChannel = false,
    publicChannel = false,
    arrivalDate = null,
    departureDate = null,
  } = {},
) {
  if (!flight) return roomOffers;
  const flightSearchPax =
    flight.flightSearchPax ?? flight.totalPax ?? countCampaignSearchPax(searchRooms);
  return enrichRoomOffersWithPackagePricing(roomOffers, flight.sellingPrice, {
    eurToIls,
    usdToIls,
    searchRooms,
    totalPax: flightSearchPax,
    flightSearchPax,
    agentChannel,
    publicChannel,
    arrivalDate,
    departureDate,
    flightDeal: flight.rawDeal ?? null,
  }).map((quote) => ({
    ...quote,
    bundledFlight: flight,
    flightSearchCapped: Boolean(flight.flightSearchCapped),
  }));
}

export function enrichRoomOffersWithPackagePricing(
  roomOffers,
  flightSellingUsd,
  {
    eurToIls,
    usdToIls,
    searchRooms,
    totalPax,
    flightSearchPax = null,
    agentChannel = false,
    publicChannel = false,
    arrivalDate = null,
    departureDate = null,
    flightDeal = null,
  } = {},
) {
  const flightUsd = Number(flightSellingUsd) || 0;
  if (!flightUsd || !Array.isArray(roomOffers) || roomOffers.length === 0) {
    return roomOffers;
  }
  const resolvedFlightPax =
    flightSearchPax ?? totalPax ?? countCampaignSearchPax(searchRooms);

  return [...roomOffers]
    .map((quote) => {
      const packageSsot = attachQuotePackagePricingSsot(quote, {
        flightUsd,
        flightDeal,
        eurToIls,
        usdToIls,
        searchRooms,
        agentChannel,
        publicChannel,
        arrivalDate,
        departureDate,
        flightSearchPax: resolvedFlightPax,
      });
      const weightedIls = packageSsot.packageWeightedIls;
      const roomBreakdown = Array.isArray(quote.roomBreakdown)
        ? enrichRoomBreakdownPackagePricing(quote.roomBreakdown, searchRooms, flightUsd, {
            eurToIls,
            usdToIls,
            agentChannel,
            publicChannel,
            flightSearchPax: resolvedFlightPax,
          })
        : quote.roomBreakdown;

      return {
        ...quote,
        packageFlightSellingUsd: flightUsd,
        packageWeightedIls: weightedIls,
        passengerPricing: packageSsot.passengerPricing,
        roomBreakdown,
      };
    })
    .sort(
      (a, b) =>
        (a.packageWeightedIls ?? Number.POSITIVE_INFINITY) -
        (b.packageWeightedIls ?? Number.POSITIVE_INFINITY),
    );
}

const AIRLINE_NAME_MAP = buildAirlineNameMap([]);

function resolveAirlineNameFromLeg(leg) {
  if (!leg) return "—";
  const code = leg.airlineId || leg.airlineCode || leg.AirlineId || leg.airline?.code;
  const direct = String(
    leg.airlineName ||
      leg.marketingAirlineName ||
      leg.AirlineName ||
      leg.airline?.name ||
      leg.marketingAirline?.name ||
      "",
  ).trim();
  // LDS often stores the IATA code in airlineName — map to full carrier name.
  if (direct && !/^[A-Z0-9]{2,3}$/i.test(direct)) {
    return direct;
  }
  return airlineDisplayNameFromCode(code || direct, AIRLINE_NAME_MAP);
}

/** LDS search deals often store legs on `flights`; card adapters read `linkedFlights`. */
function coerceCampaignFlightDeal(deal) {
  if (!deal || typeof deal !== "object") return deal;
  if (deal.linkedFlights || deal.linkedflights) return deal;
  const flights = deal.flights;
  if (!flights || typeof flights !== "object") return deal;
  return {
    ...deal,
    linkedFlights: flights.linkedFlights || flights.linkedflights || flights,
  };
}

export function buildBelvedereCampaignFlightLegRows(deal) {
  const normalized = coerceCampaignFlightDeal(deal);
  const segLegs = getFlightSegmentsWithLegs(normalized);
  const rows = buildFlightCardLegRowsFromDeal(normalized);

  return rows.map((row, index) => {
    const firstLeg = segLegs[index]?.legs?.[0] ?? null;
    const airlineName = resolveAirlineNameFromLeg(firstLeg);
    const airlineCode = row.airlineCode || firstLeg?.airlineId || firstLeg?.airlineCode || null;
    const flightNumbers = (segLegs[index]?.legs || [])
      .map((leg) => leg?.flightNumber)
      .filter(Boolean);

    return {
      ...row,
      airlineName,
      airlineCode,
      flightNumber: row.flightNumber || (flightNumbers.length ? flightNumbers.join(" · ") : null),
      flightNumbers,
      timesLabel:
        row.departureTime && row.arrivalTime
          ? `${row.departureTime} → ${row.arrivalTime}`
          : row.departureTime || row.arrivalTime || "—",
    };
  });
}

function airlineSummaryFromDeal(deal) {
  const rows = buildBelvedereCampaignFlightLegRows(deal);
  if (!rows.length) return "—";
  const names = [...new Set(rows.map((r) => r.airlineName).filter((n) => n && n !== "—"))];
  return names.length ? names.join(", ") : "—";
}

function mapDealToFlightOption(
  deal,
  index,
  {
    flightSearchPax,
    requestedPartyPax,
    flightSearchCapped = false,
    flightSearchSeatPax = null,
    requestedDestination = BELVEDERE_FLIGHT_DESTINATION,
  } = {},
) {
  const searchPax = Math.max(1, Math.floor(Number(flightSearchPax)) || 1);
  const pricing = buildFlightOfferPricingDisplay(deal, { totalPaxCount: searchPax });
  const netPrice = Number(pricing.totalFare ?? pricing.displayedPrice ?? 0) || 0;
  const markupAmount = calculateBelvedereFlightMarkup(searchPax);
  const sellingPrice = Math.round((netPrice + markupAmount) * 100) / 100;
  const currency = pricing.rawCurrency || "USD";

  const legRows = buildBelvedereCampaignFlightLegRows(deal);
  const destFromLegs = normalizeCampaignAirportCode(legRows[0]?.destinationCode);
  const originFromLegs = normalizeCampaignAirportCode(legRows[0]?.originCode);

  return {
    id: String(deal?.dealId ?? deal?.ID ?? deal?.id ?? `belvedere-flight-${index}`),
    rawDeal: deal,
    dealId: deal?.dealId ?? deal?.ID ?? deal?.id ?? null,
    linkedFlights: deal?.linkedFlights ?? deal?.linkedflights ?? null,
    extendedDealApiData: deal?.extended ?? null,
    origin: originFromLegs || deal?.depCity || deal?.origin || "",
    destination:
      destFromLegs ||
      normalizeCampaignAirportCode(deal?.arrCity || deal?.destination) ||
      requestedDestination ||
      BELVEDERE_FLIGHT_DESTINATION,
    departureDate: legRows[0]?.depDateDisplay || deal?.depDate || "",
    returnDate: legRows[1]?.depDateDisplay || deal?.retDate || "",
    routeLabel: buildRouteLabel(deal),
    airlineSummary: airlineSummaryFromDeal(deal),
    legRows,
    netPrice,
    markupAmount,
    markupPerPassengerUsd: BELVEDERE_FLIGHT_MARKUP_USD_PER_PAX,
    totalPax: searchPax,
    flightSearchPax: searchPax,
    flightSearchSeatPax:
      flightSearchSeatPax != null ? flightSearchSeatPax : searchPax,
    requestedPartyPax:
      requestedPartyPax != null ? requestedPartyPax : searchPax,
    flightSearchCapped: Boolean(flightSearchCapped),
    sellingPrice,
    currency,
    pricePerPerson: pricing.pricePerPerson,
    totalFareDisplayLabel: pricing.totalFareDisplayLabel,
    pricePerPersonDisplayLabel: pricing.pricePerPersonDisplayLabel,
  };
}

export function formatFlightMoney(amount, currency) {
  const value = Number(amount) || 0;
  if (currency === "USD") {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (currency === "EUR") {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  const formatted = new Intl.NumberFormat("he-IL", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
  return currency ? `${formatted} ${currency}` : `₪ ${formatted}`;
}

/**
 * LDS RoundTrip search TLV→HER (or chosen origin) aligned with hotel stay dates.
 *
 * When the party needs more than {@link BELVEDERE_LDS_FLIGHT_SEARCH_MAX_SEATS}
 * seats, the LDS request is capped so flights with ≥9 seats still return.
 * Hotel packages stay based on the full room party; flight fare/fees are for
 * the searchable seat capacity (no multi-PNR split in this campaign).
 */
export async function searchBelvedereFlights({
  origin,
  arrivalDate,
  departureDate,
  rooms,
  destination = BELVEDERE_FLIGHT_DESTINATION,
}) {
  const requestedCompositions = roomsToLdsCompositions(rooms);
  const searchCompositions = capBelvedereFlightSearchCompositions(requestedCompositions);
  const requestedPartyPax = countLdsCompositionTotalPax(requestedCompositions);
  const requestedSeatPax = countLdsCompositionSeatPax(requestedCompositions);
  const flightSearchSeatPax = countLdsCompositionSeatPax(searchCompositions);
  const flightSearchPax = countLdsCompositionTotalPax(searchCompositions);
  const flightSearchCapped = requestedSeatPax > BELVEDERE_LDS_FLIGHT_SEARCH_MAX_SEATS;

  const payload = {
    dealType: "RoundTrip",
    origin: origin || "TLV",
    destination: destination || BELVEDERE_FLIGHT_DESTINATION,
    dep_date: isoToLdsDate(arrivalDate),
    ret_date: isoToLdsDate(departureDate),
    room_compositions: searchCompositions,
  };

  const response = await withBelvedereFlightSearchTimeout(searchFlights(payload));
  const deals = sortFlightDealsByPerPersonAscending(
    dealsListFromSearchFlightsResponseBody(response?.data),
  );
  if (process.env.NODE_ENV === "development" && deals.length === 0 && response?.data != null) {
    console.warn("[belvedereFlightSearch] LDS returned payload but no deals parsed", {
      dataType: Array.isArray(response.data) ? "array" : typeof response.data,
      topLevelKeys:
        response.data && typeof response.data === "object" && !Array.isArray(response.data)
          ? Object.keys(response.data)
          : null,
      arrayLength: Array.isArray(response.data) ? response.data.length : null,
      requestedSeatPax,
      flightSearchSeatPax,
      flightSearchCapped,
    });
  }
  const requestedDestination = payload.destination;
  return deals.flatMap((deal, index) => {
    try {
      const mapped = mapDealToFlightOption(deal, index, {
        flightSearchPax,
        requestedPartyPax,
        flightSearchCapped,
        flightSearchSeatPax,
        requestedDestination,
      });
      if (!campaignFlightServesDestination(mapped, requestedDestination)) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[belvedereFlightSearch] skip deal — destination mismatch", {
            requestedDestination,
            got: mapped?.destination,
            origin: mapped?.origin,
            dealId: mapped?.dealId,
          });
        }
        return [];
      }
      return [mapped];
    } catch (mapErr) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[belvedereFlightSearch] skip deal — map failed", mapErr, deal);
      }
      return [];
    }
  });
}

export function buildPackagePriceBreakdown(
  hotelNet,
  roomCount,
  nights,
  selectedFlight = null,
  pricingOptions = {},
) {
  const contract = pricingOptions.contract || belvedereContract;
  const hotel = buildPriceBreakdown(
    hotelNet,
    roomCount,
    nights,
    undefined,
    contract,
    pricingOptions,
  );
  if (!selectedFlight) {
    return { ...hotel, packageTotal: hotel.hotelTotal };
  }

  const flightNet = selectedFlight.netPrice;
  const flightMarkup = selectedFlight.markupAmount;
  const flightTotal = selectedFlight.sellingPrice;

  return {
    ...hotel,
    flightNet,
    flightMarkup,
    flightTotal,
    flightCurrency: selectedFlight.currency,
    flightTotalPax: selectedFlight.totalPax ?? null,
    flightMarkupPerPaxUsd: selectedFlight.markupPerPassengerUsd ?? BELVEDERE_FLIGHT_MARKUP_USD_PER_PAX,
    packageTotal: null,
    packageWeightedIls: null,
  };
}
